import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import {
  approveFieldOrder,
  listFieldOrders,
  type FieldOrder,
} from '@/api/fieldOrders';
import { formatDate, formatINR } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { FieldOrderFormModal } from './FieldOrderFormModal';

export function FieldOrdersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(false);

  const filter = useMemo(
    () => ({ status: status || undefined }),
    [status],
  );

  const query = useQuery({
    queryKey: ['field-orders', filter],
    queryFn: () => listFieldOrders(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  const approveMut = useMutation({
    mutationFn: approveFieldOrder,
    onSuccess: (r) => {
      toast.pushStep(`Field Order approved — enquiry ${r.enquiry.enqNo}`, {
        body: 'The enquiry is now in the sales pipeline for further processing.',
        href: '/sales/enq',
        nextLabel: 'Open Enquiries',
        tag: `fo-approved-${r.fieldOrder.id}`,
      });
      qc.invalidateQueries({ queryKey: ['field-orders'] });
      qc.invalidateQueries({ queryKey: ['enquiries'] });
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  return (
    <div>
      <SectionHeader
        title="Field Sales Orders"      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!pl-5 sm:!pl-8"
              aria-label="Search field orders"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Pending Approval</option>
            <option>Approved</option>
          </Select>
        </FilterField>
        {/* Merged chip: visible on both viewports, right-aligned inside the filter block. */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ New Field Order
        </button>
      </FilterBar>

      <Table<FieldOrder>
        storageKey="field-orders"
        title="Field Orders"
        count={`${rows.length} records`}
        rows={rows}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          {
            key: 'orderNo',
            header: 'Order #',
            cell: (r) => <span className="font-extrabold text-accent">{r.orderNo}</span>,
          },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.orderDate) },
          { key: 'salesperson', header: 'Salesperson', cell: (r) => r.salesperson },
          { key: 'customer', header: 'Customer', cell: (r) => r.customerName },
          { key: 'type', header: 'Type', cell: (r) => r.customerType ?? '—' },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.items.length },
          {
            key: 'total',
            header: 'Total',
            align: 'right',
            cell: (r) => formatINR(Number(r.total)),
          },
          {
            key: 'delivery',
            header: 'Delivery',
            cell: (r) => (r.deliveryDate ? formatDate(r.deliveryDate) : '—'),
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => (
              <Badge
                tone={
                  r.status === 'Approved'
                    ? 'success'
                    : r.status === 'Pending Approval'
                      ? 'warning'
                      : 'danger'
                }
              >
                {r.status}
              </Badge>
            ),
          },
          {
            key: 'enq',
            header: 'ENQ',
            cell: (r) =>
              r.enqId ? <Badge tone="accent">ENQ #{r.enqId}</Badge> : '—',
          },
          {
            key: 'actions',
            header: '',
            width: '120px',
            cell: (r) =>
              r.status === 'Pending Approval' && r.customerId ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => approveMut.mutate(r.id)}
                  disabled={approveMut.isPending}
                >
                  Approve
                </Button>
              ) : null,
          },
        ]}
      />

      <FieldOrderFormModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
